import React, { CSSProperties, useCallback, useMemo, useRef } from 'react';
import { Alert, Card, Input, Select, Switch, Typography } from '@duxton-web/ui';
import { styled } from 'styled-components';

import {
  useVariableContext,
  useVariableDispatchContext,
} from '../../contexts/variable-context';
import { variableDetailUpdate } from '../../store/actions/variable/action-creators';
import { CANARY_ELIGIBLE_ASSIGN_BY, isCanaryEligible } from './eligibility';
import { resolveCanaryEnabled } from './enablement-utils';
import MetricsSection from './metrics-section';
import MonitoringGuidance from './monitoring-guidance';
import MonitorsSection from './monitors-section';
import { DEFAULT_CANARY_PHASES, normalizeCanaryPhases } from './phase-utils';
import PhasesSection from './phases-section';
import {
  CanaryConfig,
  CanaryPhase,
  DatadogMonitor,
  MonitorMetric,
} from './types';
import {
  SyncedFromGrabOverlay,
  useIsSyncedFromGrab,
} from '@/features/tenant-sync';
import { VariableInfo } from '@/lib/PlannerDTOs';

export { CANARY_ELIGIBLE_ASSIGN_BY, isCanaryEligible };

export const DISABLED_REASON_OPTIONS = [
  { value: 'no_metrics', label: 'No metrics available' },
  { value: 'low_risk', label: 'Low risk change' },
  { value: 'already_full_rollout', label: 'Already at full rollout' },
  { value: 'rollback', label: 'Rollback operation' },
  { value: 'urgent', label: 'Urgent change' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'other', label: 'Other' },
];

const getDefaultCanaryConfig = (): CanaryConfig => ({
  enabled: true,
  phases: DEFAULT_CANARY_PHASES.map((phase) => ({ ...phase })),
  metrics: [],
  monitors: [],
});

export const mapVariableCanaryToConfig = (
  variable: VariableInfo | undefined,
): CanaryConfig => {
  if (variable?.metadata?.canaryConfig) {
    const cfg = variable.metadata.canaryConfig;
    const rawMetrics: any[] = cfg.metrics ?? [];

    // New serialised format: metrics[] may contain both query and monitor rows.
    // Split them back into separate UI arrays.
    const hasMixedTypes = rawMetrics.some((m) => m.type != null);
    if (hasMixedTypes) {
      const queryMetrics = rawMetrics
        .filter((m) => m.type === 'query' || m.type == null)
        .map((m) => ({
          id: crypto.randomUUID(),
          displayName: m.displayName ?? '',
          query: m.query ?? '',
          min: m.min,
          max: m.max,
        }));
      const monitors = rawMetrics
        .filter((m) => m.type === 'monitor' && !!m.monitorUrl)
        .map((m) => ({
          id: crypto.randomUUID(),
          displayName: m.displayName,
          monitorUrl: m.monitorUrl,
        }));
      const defaults = getDefaultCanaryConfig();
      return {
        ...cfg,
        enabled: resolveCanaryEnabled(variable),
        phases: normalizeCanaryPhases(cfg.phases ?? defaults.phases),
        metrics: queryMetrics,
        monitors,
      };
    }

    // Legacy format: separate monitors[] array.
    // Filter out stale empty backend rows (no UI id, no URL).
    const rawMonitors: any[] = (cfg as any).monitors ?? [];
    const monitors = rawMonitors.filter((m) => m.id != null || !!m.monitorUrl);
    // Always ensure phases and metrics are present — disabled configs saved to
    // the backend omit them, which would crash validateCanaryConfig.
    const defaults = getDefaultCanaryConfig();
    return {
      ...cfg,
      enabled: resolveCanaryEnabled(variable),
      phases: normalizeCanaryPhases(cfg.phases ?? defaults.phases),
      metrics: rawMetrics,
      monitors,
    };
  }
  if (variable?.canary) {
    const { canary } = variable;
    return {
      enabled: resolveCanaryEnabled(variable),
      phases: normalizeCanaryPhases(
        canary.phases?.map((p) => ({
          targetPercentage: p.percentage,
          durationMins: p.duration_minutes,
        })),
      ),
      metrics: (canary.metrics ?? []).map((m) => ({
        displayName: m.name,
        query: m.query,
        min: m.thresholds?.min,
        max: m.thresholds?.max,
      })),
      monitors: [],
    };
  }
  return getDefaultCanaryConfig();
};

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export const validateCanaryConfig = (
  config: CanaryConfig,
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate phases
  if (config.phases.length < 3) {
    errors.push('At least 3 canary phases are required.');
  }

  if (
    config.phases.length > 0 &&
    config.phases[config.phases.length - 1]?.targetPercentage !== 100
  ) {
    errors.push('The final canary phase must target 100%.');
  }

  config.phases.forEach((phase, index) => {
    if (phase.targetPercentage < 1 || phase.targetPercentage > 100) {
      errors.push(
        `Phase ${index + 1}: Percentage must be between 1% and 100%.`,
      );
    }
    if (phase.durationMins < 1) {
      errors.push(`Phase ${index + 1}: Duration must be at least 1 minute.`);
    }
    if (!Number.isInteger(phase.targetPercentage)) {
      warnings.push(`Phase ${index + 1}: Percentage should be a whole number.`);
    }
    if (phase.durationMins > 10080) {
      // 7 days
      warnings.push(
        `Phase ${index + 1}: Duration exceeds 7 days (10,080 minutes).`,
      );
    }
  });

  // Check for ascending percentage order
  for (let i = 1; i < config.phases.length; i++) {
    if (
      config.phases[i].targetPercentage <= config.phases[i - 1].targetPercentage
    ) {
      warnings.push(
        `Phase ${i + 1}: Percentage should be higher than the previous phase for a proper rollout sequence.`,
      );
    }
  }

  // Support both split format (separate monitors[]) and merged format
  // (metrics[] contains both query and monitor rows differentiated by type).
  const rawMetrics: any[] = config.metrics ?? [];
  const queryOnlyMetrics = rawMetrics.filter(
    (m) => !m.type || m.type === 'query',
  );
  const monitorRowsInMetrics = rawMetrics.filter((m) => m.type === 'monitor');
  const allMonitors = [...(config.monitors ?? []), ...monitorRowsInMetrics];

  const hasMetrics = queryOnlyMetrics.length > 0;
  const hasMonitors = allMonitors.length > 0;

  // Require at least one metric query or Datadog monitor when canary is enabled
  if (!hasMetrics && !hasMonitors) {
    errors.push(
      'At least one monitoring metric or Datadog monitor is required.',
    );
  }

  // Validate each query metric (skip monitor-type rows — they have no query field)
  queryOnlyMetrics.forEach((metric, index) => {
    if (!(metric.displayName ?? '').trim()) {
      errors.push(`Metric ${index + 1}: Display name is required.`);
    }
    if (!(metric.query ?? '').trim()) {
      errors.push(`Metric ${index + 1}: Datadog query is required.`);
    }
    if (metric.queryValidated === false) {
      errors.push(
        `Metric ${index + 1} (${metric.displayName || 'unnamed'}): Query validation failed — fix the query and try saving again.`,
      );
    }
    if (metric.min != null && metric.max != null && metric.min >= metric.max) {
      errors.push(
        `Metric ${index + 1}: Minimum threshold must be less than maximum threshold.`,
      );
    }
    if ((metric.displayName ?? '').length > 100) {
      warnings.push(
        `Metric ${index + 1}: Display name is quite long. Consider shortening it.`,
      );
    }
    if ((metric.query ?? '').length > 1000) {
      warnings.push(
        `Metric ${index + 1}: Datadog query is very long. Ensure it's optimized.`,
      );
    }
  });

  // Validate each monitor (from both split and merged format)
  allMonitors.forEach((monitor: any, index) => {
    const url = monitor.monitorUrl ?? '';
    if (!url.trim()) {
      errors.push(`Monitor ${index + 1}: A Monitor URL is required.`);
    }
    if (monitor.monitorValidated === false) {
      errors.push(
        `Monitor ${index + 1}: Validation failed — fix the monitor URL and try saving again.`,
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

interface CanarySetupWrapperProps {
  enable: boolean;
}

const CanarySetupWrapper: React.FC<CanarySetupWrapperProps> = ({ enable }) => {
  const {
    variable,
    disabled: contextDisabled,
    variableIsValid,
  } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  // Check if variable is synced from Grab (tenant portal)
  const isSyncedFromGrab = useIsSyncedFromGrab(variable);
  const disabled = contextDisabled || isSyncedFromGrab;

  const variableRef = useRef(variable);
  variableRef.current = variable;
  const validRef = useRef(variableIsValid);
  validRef.current = variableIsValid;

  // Derive config directly from the store instead of mirroring it into local
  // state. Edits flush to the store synchronously via flushToStore, so the
  // memo always reflects the latest value without a stale-closure risk.
  const localConfig = useMemo(
    () => mapVariableCanaryToConfig(variable),
    [variable?.metadata?.canaryConfig, variable?.canary],
  );

  const validation = useMemo(() => {
    return validateCanaryConfig(localConfig);
  }, [localConfig]);

  const flushToStore = useCallback(
    (config: CanaryConfig) => {
      const v = variableRef.current;
      dispatch(
        variableDetailUpdate(
          {
            ...v,
            metadata: {
              ...v?.metadata,
              canaryConfig: config,
            },
          } as VariableInfo,
          validRef.current,
        ),
      );
    },
    [dispatch],
  );

  const handlePhasesChange = (phases: CanaryPhase[]) => {
    flushToStore({ ...localConfig, phases });
  };

  const handleMetricsChange = (metrics: MonitorMetric[]) => {
    flushToStore({ ...localConfig, metrics });
  };

  const handleMonitorsChange = (monitors: DatadogMonitor[]) => {
    flushToStore({ ...localConfig, monitors });
  };

  if (!enable) return null;

  return (
    <CanaryContent>
      {validation.errors.length > 0 && (
        <Alert
          type="error"
          message="Canary Configuration Errors"
          description={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: '16px' }}
        />
      )}

      {validation.warnings.length > 0 && (
        <Alert
          type="warning"
          message="Canary Configuration Warnings"
          description={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {validation.warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: '16px' }}
        />
      )}

      <PhasesSection
        phases={localConfig.phases}
        onChange={handlePhasesChange}
        disabled={disabled}
      />

      <MonitoringGuidance />

      <MetricsSection
        metrics={localConfig.metrics}
        onChange={handleMetricsChange}
        disabled={disabled}
        variableName={variable?.name ?? ''}
      />

      <MonitorsSection
        monitors={localConfig.monitors ?? []}
        onChange={handleMonitorsChange}
        disabled={disabled}
        variableName={variable?.name ?? ''}
      />
    </CanaryContent>
  );
};

interface CanaryProps {
  style?: CSSProperties;
}

const Canary: React.FC<CanaryProps> = ({ style }) => {
  const {
    variable,
    disabled: contextDisabled,
    variableIsValid,
  } = useVariableContext();
  const dispatch = useVariableDispatchContext();

  // Check if variable is synced from Grab (tenant portal)
  const isSyncedFromGrab = useIsSyncedFromGrab(variable);
  const disabled = contextDisabled || isSyncedFromGrab;

  const currentConfig = useMemo(
    () => mapVariableCanaryToConfig(variable),
    [variable?.metadata?.canaryConfig, variable?.canary],
  );
  const isCanaryEnabled = currentConfig.enabled;

  // The reason row is always visible when canary is off — a reason is
  // required in both the edit and create flows whenever enabled=false.
  const showReasonRow = !isCanaryEnabled;

  const handleCanaryToggle = (enabled: boolean) => {
    const canaryConfig = enabled
      ? {
          ...currentConfig,
          enabled: true,
          disabledReason: undefined,
          disabledReasonNote: undefined,
        }
      : { ...currentConfig, enabled: false };

    dispatch(
      variableDetailUpdate(
        {
          ...variable,
          metadata: {
            ...variable?.metadata,
            canaryConfig,
          },
        } as VariableInfo,
        variableIsValid,
      ),
    );
  };

  const handleDisabledReasonChange = (reason: string) => {
    dispatch(
      variableDetailUpdate(
        {
          ...variable,
          metadata: {
            ...variable?.metadata,
            canaryConfig: {
              ...currentConfig,
              disabledReason: reason,
              // Clear the free-text when switching away from 'other'
              disabledReasonNote:
                reason === 'other'
                  ? currentConfig.disabledReasonNote
                  : undefined,
            },
          },
        } as VariableInfo,
        variableIsValid,
      ),
    );
  };

  const handleDisabledReasonOtherChange = (text: string) => {
    dispatch(
      variableDetailUpdate(
        {
          ...variable,
          metadata: {
            ...variable?.metadata,
            canaryConfig: { ...currentConfig, disabledReasonNote: text },
          },
        } as VariableInfo,
        variableIsValid,
      ),
    );
  };

  return (
    <Card style={style}>
      <CanaryContainer>
        <SyncedFromGrabOverlay show={isSyncedFromGrab} />
        <EnableCanaryContainer>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                size="small"
                data-testid="canary-enable"
                checked={isCanaryEnabled}
                disabled={disabled}
                onChange={handleCanaryToggle}
              />
            </div>
            <Typography.Text>Enable Canary Deployment</Typography.Text>
          </div>

          <div style={{ display: 'flex', gap: 4 }}>
            <SecondaryText>
              Configure canary deployment phases and monitoring metrics for
              gradual rollouts with automatic monitoring and rollback
              capabilities.
            </SecondaryText>
          </div>

          {showReasonRow && (
            <DisabledReasonSection>
              <DisabledReasonRow>
                <Typography.Text
                  style={{ fontSize: 12, color: 'rgba(0,0,0,0.85)' }}
                >
                  Reason:
                </Typography.Text>
                <Select
                  size="small"
                  placeholder="Why is canary disabled?"
                  value={currentConfig.disabledReason || undefined}
                  onChange={handleDisabledReasonChange}
                  disabled={disabled}
                  allowClear
                  style={{ flex: 1, maxWidth: 280 }}
                  options={DISABLED_REASON_OPTIONS}
                />
              </DisabledReasonRow>
              {currentConfig.disabledReason === 'other' && (
                <Input.TextArea
                  placeholder="Please describe why canary is not applicable (max 200 characters)"
                  value={currentConfig.disabledReasonNote ?? ''}
                  onChange={(e) =>
                    handleDisabledReasonOtherChange(e.target.value)
                  }
                  disabled={disabled}
                  maxLength={200}
                  showCount
                  rows={3}
                  style={{ marginTop: 4 }}
                />
              )}
            </DisabledReasonSection>
          )}
        </EnableCanaryContainer>

        <CanarySetupWrapper enable={isCanaryEnabled} />
      </CanaryContainer>
    </Card>
  );
};

const CanaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

const EnableCanaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
`;

const CanaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SecondaryText = styled(Typography.Text)`
  color: #a3a3a3;
  font-size: 12px;
`;

const DisabledReasonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 4px;
`;

const DisabledReasonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export default Canary;
